import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Pill } from "./PortalPage";
import { DecisionDialog } from "./DecisionDialog";
import { fmtMoney, fmtDate, QUOTE_STATUS } from "./portalUtils";
import { downloadAuthFile } from "../../lib/fileOpen";
import { DownloadSimple, Receipt } from "@phosphor-icons/react";
import { QuoteNarrative } from "./QuoteNarrative";
import { toast } from "sonner";

/* Portal job page: extra-works quotes the team has pushed for this job — approve / decline right here. */
export function VariationQuotes({ quotes = [], canApprove, onChanged }) {
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!quotes.length) return <p className="text-xs text-muted-foreground" data-testid="variations-empty">No additional-works quotes on this job.</p>;

  const respond = async ({ notes, po_number }) => {
    setBusy(true);
    try {
      await API.post(`/client-hub/quotes/${decision.id}/respond`, { decision: decision.kind, notes, po_number });
      toast.success(decision.kind === "approve" ? "Variation approved — we'll add it to the job" : "Variation declined");
      setDecision(null); onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="variation-quotes">
      {quotes.map((q) => {
        const awaiting = q.status === "sent";
        return (
          <div key={q.id} className={`border rounded-lg p-4 ${awaiting ? "border-purple-300 dark:border-purple-800 bg-purple-50/60 dark:bg-purple-950/20" : "border-border bg-card"}`} data-testid={`variation-${q.id}`}>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Receipt size={14} className="text-muted-foreground" />
                  <span className="text-[10px] font-mono text-muted-foreground">{q.reference}</span>
                  <Pill map={QUOTE_STATUS} value={q.status} />
                  {awaiting && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-600 text-white font-semibold" data-testid={`variation-needs-approval-${q.id}`}>NEEDS YOUR APPROVAL</span>}
                </div>
                <p className="text-sm font-semibold">{q.title}</p>
                <p className="text-lg font-bold font-[Chivo]" data-testid={`variation-total-${q.id}`}>{fmtMoney(q.grand_total)} <span className="text-xs font-normal text-muted-foreground">ex VAT{q.vat_amount ? ` · ${fmtMoney(q.total_inc_vat)} inc VAT` : ""}</span></p>
                {q.line_items?.length > 0 && (
                  <ul className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
                    {q.line_items.slice(0, 4).map((li, i) => <li key={i}>• {li.description || li.item_name} × {li.quantity} — {fmtMoney(li.total)}</li>)}
                    {q.line_items.length > 4 && <li>+ {q.line_items.length - 4} more lines</li>}
                  </ul>
                )}
                <QuoteNarrative quote={q} className="mt-2" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Sent {fmtDate(q.sent_at)}{q.validity_days ? ` · valid ${q.validity_days} days` : ""}
                  {q.client_response && ` · ${q.status === "declined" ? "Declined" : "Approved"} by ${q.client_response.name} ${fmtDate(q.client_response.at)}${q.client_response.po_number ? ` · PO ${q.client_response.po_number}` : ""}`}
                </p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => downloadAuthFile(`/api/client-hub/quotes/${q.id}/pdf`, `${q.reference}.pdf`)} data-testid={`variation-pdf-${q.id}`}><DownloadSimple size={14} className="mr-1" /> PDF</Button>
                {awaiting && (canApprove ? (
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => setDecision({ id: q.id, kind: "approve", total: q.grand_total, label: q.title })} data-testid={`variation-approve-${q.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300 flex-1" onClick={() => setDecision({ id: q.id, kind: "decline", total: q.grand_total, label: q.title })} data-testid={`variation-decline-${q.id}`}>Decline</Button>
                  </div>
                ) : <p className="text-[11px] text-muted-foreground max-w-[200px]">Only portal administrators can approve quotes.</p>)}
              </div>
            </div>
          </div>
        );
      })}
      <DecisionDialog decision={decision} onClose={() => setDecision(null)} onConfirm={respond} busy={busy} testPrefix="variation-decision" />
    </div>
  );
}
